import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest, filter, map, take, tap } from 'rxjs';

import { ElementActions, ElementTypes, GaContext, MediaQueryService, OfferListFetchParams } from '@fcom/common';
import {
  FlightFilters,
  FlightTimeOption,
  defaultFilterValues,
} from '@fcom/common-booking/interfaces/flight-filters.interface';
import { GtmService } from '@fcom/common/gtm/services/gtm.service';
import { Cabin } from '@fcom/dapi/api/models';
import { getLocation } from '@fcom/locations';
import {
  ButtonSize,
  ButtonTheme,
  ButtonType,
  CheckBoxTheme,
  IconPosition,
  TabTheme,
  TooltipOptions,
  TooltipPosition,
  TooltipService,
} from '@fcom/ui-components';
import { unsubscribe } from '@fcom/utils';
import { TicketSelectionPhase } from '@fcom/voluntary-change/enums';
import { AirOffersStatus, BookingAppState, SelectionPhase } from '@fcom/booking';
import { BookingAirBoundsService } from '@fcom/booking/modules/ticket-selection/services';

@Component({
  selector: 'fin-flight-selection-filters',
  styleUrls: ['./flight-selection-filters.component.scss'],
  templateUrl: './flight-selection-filters.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlightSelectionFiltersComponent implements OnInit, OnChanges, OnDestroy {
  /**
   * Whether radio buttons in travel class filter should be disabled
   */
  @Input()
  isTravelClassChoicesDisabled: boolean;

  @Input()
  selectionPhase: SelectionPhase | TicketSelectionPhase;

  @Input()
  showRefundability = true;

  @Input()
  travelClasses$: Observable<Cabin[]>;

  /**
   * How many results the current filters produce
   * In desktop version used in all dropdowns' footer buttons
   * In mobile used in modal's footer button
   */
  @Input()
  resultsAmount: number;

  /**
   * Amount of layovers each flight has.
   * Used in stops filter
   */
  @Input()
  layoverAmounts: number[] = [];

  /**
   * Used in times filter
   */
  @Input()
  originLocationCode: string;

  /**
   * Used in times filter
   */
  @Input()
  destinationLocationCode: string;

  @Input() filters$: Observable<FlightFilters>;

  @Input()
  cabinLastRequestParam$: Observable<{ cabin: Cabin }>;

  @Input()
  isTravelClassDisabled = false;

  @Input()
  showFlightSelectionFilters: boolean;

  @Output()
  setFilter: EventEmitter<FlightFilters> = new EventEmitter();
  @Output()
  resetFilter: EventEmitter<keyof FlightFilters> = new EventEmitter();
  @Output()
  resetAllFilters: EventEmitter<void> = new EventEmitter();
  @Output()
  showSpinner: EventEmitter<void> = new EventEmitter();

  selectedStopsOption: number = defaultFilterValues.defaultStopsOption;
  selectedRefundabilityOption: boolean = defaultFilterValues.defaultRefundabilityOption;
  selectedTravelClass: Cabin;
  selectedDepartureTimes: FlightTimeOption[] = defaultFilterValues.defaultTimeOptions;
  selectedArrivalTimes: FlightTimeOption[] = defaultFilterValues.defaultTimeOptions;
  defaultInboundTravelClass: Cabin;

  departureCityName: string;
  arrivalCityName: string;

  isModalOpen: boolean;

  readonly ButtonTheme = ButtonTheme;
  readonly ButtonType = ButtonType;
  readonly CheckBoxTheme = CheckBoxTheme;
  readonly ButtonSize = ButtonSize;
  readonly IconPosition = IconPosition;
  readonly TabTheme = TabTheme;
  readonly FlightTimeOption = FlightTimeOption;
  readonly Cabin = Cabin;
  readonly AirOffersStatus = AirOffersStatus;
  readonly SelectionPhase = SelectionPhase;
  readonly TicketSelectionPhase = TicketSelectionPhase;

  tooltipOptions: TooltipOptions = {
    tooltipID: 'flight-selection-filters',
    openByDefault: false,
    closeOnScroll: false,
    showArrowCaret: false,
    showLeftBorder: false,
    tooltipPosition: TooltipPosition.BOTTOM,
    roundedCorners: true,
  };

  activeTab$ = new BehaviorSubject(0);
  showStopsFilter$: Observable<boolean>;
  awardQueryParams$: Observable<OfferListFetchParams>;
  isMobile$: Observable<boolean>;
  showClearAllBtn: boolean;
  shouldNotClearTravelClass: boolean;
  selectedFiltersLength$: BehaviorSubject<number> = new BehaviorSubject(0);
  resizedWatcher$: Subject<void> = new Subject();
  subscriptions: Subscription = new Subscription();

  constructor(
    private store$: Store<BookingAppState>,
    private tooltipService: TooltipService,
    private mediaQueryService: MediaQueryService,
    private gtmService: GtmService,
    private bookingAirBoundsService: BookingAirBoundsService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.cabinLastRequestParam$
        ?.pipe(
          filter(Boolean),
          take(1),
          tap((params) => {
            if (this.selectionPhase === SelectionPhase.OUTBOUND) {
              this.setFilter.emit({ cabin: params.cabin });
            }
          })
        )
        .subscribe()
    );

    this.subscriptions.add(
      this.travelClasses$
        .pipe(
          tap((travelClasses) => {
            const defaultIsNotMixedCabinForInbound =
              this.selectionPhase === SelectionPhase.INBOUND && this.defaultInboundTravelClass !== Cabin.MIXED;
            this.shouldNotClearTravelClass =
              this.isTravelClassDisabled || defaultIsNotMixedCabinForInbound || travelClasses.length === 1;
          }),
          take(1)
        )
        .subscribe()
    );
    this.subscriptions.add(this.filters$.pipe(tap(this.handleFilterChange.bind(this))).subscribe());

    this.showStopsFilter$ = this.shouldShowStopsFilter();
    this.isMobile$ = this.mediaQueryService.isBreakpoint$('mobile');
    this.updateLocations();
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  ngOnChanges(): void {
    this.updateLocations();
  }

  handleFilterChange(filters: FlightFilters): void {
    if (
      this.selectionPhase === SelectionPhase.OUTBOUND &&
      this.selectedTravelClass &&
      filters.cabin !== this.selectedTravelClass
    ) {
      this.subscriptions.add(
        this.activatedRoute.queryParams.pipe(take(1)).subscribe((p) => {
          const changedJson = JSON.stringify({
            ...JSON.parse(p.json),
            cabin: filters.cabin,
          });
          const navigationExtras: NavigationExtras = {
            queryParams: {
              json: changedJson,
            },
            queryParamsHandling: 'merge',
          };
          this.router.navigate([], navigationExtras);
        })
      );
    }

    if (
      this.selectedTravelClass &&
      this.selectedTravelClass !== filters.cabin &&
      this.selectionPhase === SelectionPhase.OUTBOUND
    ) {
      this.refetchBounds(filters.cabin);
    }

    this.selectedStopsOption = filters.stopsCount;
    this.selectedRefundabilityOption = filters.refundable;
    this.selectedTravelClass = filters.cabin;
    this.selectedDepartureTimes = filters.departureTime;
    this.selectedArrivalTimes = filters.arrivalTime;

    if (this.selectionPhase === SelectionPhase.INBOUND && !this.defaultInboundTravelClass) {
      this.defaultInboundTravelClass = filters.cabin;
    }

    this.updateSelectedFiltersLength();
    this.updateClearAllButton();
  }

  onSelectStopsCount(stopsCount: number): void {
    this.showSpinner.emit();
    this.setFilter.emit({ stopsCount });
    this.updateCarouselWidth();

    this.gtmService.trackElement(
      `booking-flow-select-filter-stops-${stopsCount}`,
      GaContext.BOOKING_FLOW,
      ElementTypes.RADIO,
      undefined,
      ElementActions.CLICK
    );
  }

  updateClearAllButton(): void {
    // Check if selected stops option is not the default
    const isNotDefaultStopsOption = this.selectedStopsOption !== defaultFilterValues.defaultStopsOption;

    // Check if selected refundability option is not the default
    const isNotDefaultRefundabilityOption =
      this.selectedRefundabilityOption &&
      this.selectedRefundabilityOption !== defaultFilterValues.defaultRefundabilityOption;

    // Check if selected travel class is not the default and the flag allows clearing
    const isNotDefaultTravelClass =
      this.selectedTravelClass !== defaultFilterValues.defaultTravelClass && this.shouldNotClearTravelClass === false;

    // Check if selected departure times or arrival times are less than 3
    const isLessThanThreeDepartureTimes = this.selectedDepartureTimes?.length < 3;
    const isLessThanThreeArrivalTimes = this.selectedArrivalTimes?.length < 3;

    // Set the clear button visibility based on the conditions
    this.showClearAllBtn =
      isNotDefaultStopsOption ||
      isNotDefaultRefundabilityOption ||
      isNotDefaultTravelClass ||
      isLessThanThreeDepartureTimes ||
      isLessThanThreeArrivalTimes;
  }

  updateSelectedFiltersLength(): void {
    const selectedFilters = [
      this.selectedStopsOption !== defaultFilterValues.defaultStopsOption,
      this.selectedRefundabilityOption !== defaultFilterValues.defaultRefundabilityOption &&
        this.selectedRefundabilityOption !== undefined,
      this.selectedTravelClass !== defaultFilterValues.defaultTravelClass && this.selectedTravelClass !== undefined,
      (this.selectedDepartureTimes?.length ?? 0) < defaultFilterValues.defaultTimeOptions.length,
      (this.selectedArrivalTimes?.length ?? 0) < defaultFilterValues.defaultTimeOptions.length,
    ].filter(Boolean).length;

    this.selectedFiltersLength$.next(selectedFilters);
  }

  onSelectRefundability(refundable: boolean): void {
    this.showSpinner.emit();
    this.setFilter.emit({ refundable });
    this.updateCarouselWidth();

    this.gtmService.trackElement(
      `booking-flow-select-filter-refundability-${refundable ? 'refundable' : 'nonrefundable'}`,
      GaContext.BOOKING_FLOW,
      ElementTypes.RADIO,
      undefined,
      ElementActions.CLICK
    );
  }

  onSelectTravelClass(cabin: Cabin): void {
    this.showSpinner.emit();
    this.setFilter.emit({ cabin });
    this.updateCarouselWidth();

    this.gtmService.trackElement(
      `booking-flow-select-filter-class-${cabin.toLowerCase()}`,
      GaContext.BOOKING_FLOW,
      ElementTypes.RADIO,
      undefined,
      ElementActions.CLICK
    );
  }

  private refetchBounds(cabin: Cabin) {
    const queryParams = this.bookingAirBoundsService.getLastRequestParams();

    if (this.selectionPhase === SelectionPhase.OUTBOUND && !queryParams?.isAward) {
      return this.bookingAirBoundsService.triggerFetchBounds({ ...queryParams, cabin });
    } else {
      return null;
    }
  }

  clearFilter(flightFilter: keyof FlightFilters): void {
    this.showSpinner.emit();
    this.updateCarouselWidth();
    this.resetFilter.emit(flightFilter);
  }

  clearAllFilters(contextForAnalytics: 'desktop' | 'mobile' | 'empty-state'): void {
    this.showSpinner.emit();

    this.resetAllFilters.emit();

    this.updateCarouselWidth();

    this.gtmService.trackElement(
      `booking-flow-clear-all-filters-${contextForAnalytics}`,
      GaContext.BOOKING_FLOW,
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK
    );
  }

  onSelectDepartureTime(departureTime: FlightTimeOption): void {
    const newDepartureTime = this.selectedDepartureTimes?.includes(departureTime)
      ? this.selectedDepartureTimes?.filter((d) => d !== departureTime)
      : [...this.selectedDepartureTimes, departureTime];

    this.showSpinner.emit();
    this.setFilter.emit({ departureTime: newDepartureTime });

    this.updateCarouselWidth();

    this.gtmService.trackElement(
      `booking-flow-select-filter-departure-${departureTime.toLowerCase()}`,
      GaContext.BOOKING_FLOW,
      ElementTypes.CHECKBOX,
      undefined,
      ElementActions.CLICK
    );
  }

  onSelectArrivalTime(arrivalTime: FlightTimeOption): void {
    const newArrivalTime = this.selectedArrivalTimes?.includes(arrivalTime)
      ? this.selectedArrivalTimes.filter((d) => d !== arrivalTime)
      : [...this.selectedArrivalTimes, arrivalTime];

    this.showSpinner.emit();
    this.setFilter.emit({ arrivalTime: newArrivalTime });

    this.updateCarouselWidth();

    this.gtmService.trackElement(
      `booking-flow-select-filter-arrival-${arrivalTime.toLowerCase()}`,
      GaContext.BOOKING_FLOW,
      ElementTypes.CHECKBOX,
      undefined,
      ElementActions.CLICK
    );
  }

  shouldShowStopsFilter(): Observable<boolean> {
    return this.filters$.pipe(
      take(1),
      map((filters) => {
        if (typeof filters?.stopsCount === 'number') {
          return true;
        }

        const hasDirectFlights = this.layoverAmounts.some((value) => value === 0);
        const hasMultipleStops = this.layoverAmounts.some((value) => value > 0);

        return hasDirectFlights && hasMultipleStops;
      })
    );
  }

  updateLocations(): void {
    this.subscriptions.add(
      combineLatest([
        this.store$.select(getLocation(this.originLocationCode)),
        this.store$.select(getLocation(this.destinationLocationCode)),
      ])
        .pipe(take(1))
        .subscribe(([origin, destination]) => {
          this.departureCityName = origin?.cityName;
          this.arrivalCityName = destination?.cityName;
        })
    );
  }

  updateCarouselWidth(): void {
    this.resizedWatcher$.next();
  }

  closeTooltip(): void {
    this.tooltipService.close(true);
  }

  keepOrder(_a: unknown, _b: unknown): number {
    return 0;
  }

  getTimeRange(option: FlightTimeOption): string {
    switch (option) {
      case FlightTimeOption.MORNING:
        return '00:00 - 11:59 h';
      case FlightTimeOption.AFTERNOON:
        return '12:00 - 16:59 h';
      case FlightTimeOption.EVENING:
        return '17:00 - 23:59 h';
      default:
        return '';
    }
  }

  setActiveTimesTab({ index }: { index: number; data: unknown }): void {
    this.activeTab$.next(index);
  }

  showModal(): void {
    this.isModalOpen = true;
  }

  closeModal(): void {
    this.isModalOpen = false;
  }

  sendAnalytics(contextForAnalytics: string): void {
    this.gtmService.trackElement(
      `booking-flow-open-filters-${contextForAnalytics}`,
      GaContext.BOOKING_FLOW,
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK
    );
  }
}
